import { useLogging } from "@/components/contexts/LoggingContext";
import { APIResponse } from "@simplerealtybits/srb-types";
import { LoginResponse } from "@t/LoginResponse";
import { MfaSetupDto } from "@t/MfaSetupDto";
import { UserGet } from "@t/UserGet";
import { ValidateCredentialsDto } from "@t/ValidateCredentialsDto";
import { createContext, useContext, useEffect, useState } from "react";
import { getDeviceToken, setDeviceToken } from "@/utils/trustedDevice";

const bypassModes = ["development", "staging", "devdeployed"];
const enableDevMfaBypass = bypassModes.includes(import.meta.env.MODE);

interface IAuthContext {
    authState: LoginResponse;
    login: (userName: string, passsword: string) => Promise<APIResponse<ValidateCredentialsDto>>;
    devLogin?: (email: string, password: string) => Promise<LoginResponse>;
    logout: () => void;
    updateUser: (user: UserGet) => void;
    requestNewPassword: (email: string) => Promise<APIResponse<boolean>>;
    resetPassword: (password: string, token: string) => Promise<APIResponse<boolean>>;
    validateResetToken: (token: string) => Promise<APIResponse<boolean>>;
    startMfaSetup: (email: string) => Promise<APIResponse<MfaSetupDto>>;
    verifyMfaSetup: (
        code: string,
        secret: string,
        email: string,
        rememberDevice?: boolean
    ) => Promise<APIResponse<LoginResponse>>;
    verifyMfa: (
        code: string,
        email: string,
        rememberDevice?: boolean
    ) => Promise<APIResponse<LoginResponse>>;
    isLoggingOut: boolean;
}

const defaultAuthContext: IAuthContext = {
    authState: {
        token: "",
        user: {} as UserGet,
    },
    login: (async () => { }) as unknown as (
        userName: string,
        passsword: string
    ) => Promise<APIResponse<ValidateCredentialsDto>>,
    devLogin: enableDevMfaBypass
        ? async (email: string, password: string) => {
            const baseUrl = import.meta.env.VITE_API_URL as string;
            return fetch(`${baseUrl}/api/v1/auth/dev-login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ emailAddress: email, password }),
            })
                .then((response) => response.json())
                .then((data: APIResponse<LoginResponse>) => {
                    if (data.success === false) throw new Error(data.message);
                    return data.data;
                });
        }
        : undefined,
    logout: () => { },
    updateUser: () => { },
    requestNewPassword: (async () => { }) as unknown as (
        email: string
    ) => Promise<APIResponse<boolean>>,
    resetPassword: (async () => { }) as unknown as (
        password: string,
        token: string
    ) => Promise<APIResponse<boolean>>,
    validateResetToken: (async () => { }) as unknown as (
        token: string
    ) => Promise<APIResponse<boolean>>,
    startMfaSetup: (async () => { }) as unknown as (
        email: string
    ) => Promise<APIResponse<MfaSetupDto>>,
    verifyMfaSetup: (async () => { }) as unknown as (
        code: string,
        secret: string,
        email: string
    ) => Promise<APIResponse<LoginResponse>>,
    verifyMfa: (async () => { }) as unknown as (
        code: string,
        email: string
    ) => Promise<APIResponse<LoginResponse>>,
    // Default to false for isLoggingOut
    isLoggingOut: false,
};

const AuthContext = createContext<IAuthContext>(defaultAuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const { setLoggingParams } = useLogging();
    const baseUrl = import.meta.env.VITE_API_URL as string;
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const [authState, setAuthState] = useState<LoginResponse>(() => {
        const savedState = localStorage.getItem("authState");

        let parsedState: LoginResponse | null = null;

        if (savedState) {
            try {
                parsedState = JSON.parse(savedState) as LoginResponse;
                setLoggingParams({
                    user: {
                        id: parsedState.user.userId,
                        email: parsedState.user.emailAddress,
                        name: parsedState.user.fullName,
                        role: parsedState.user.userRole,
                    },
                });
                return parsedState;
            } catch (error) {
                console.error("Failed to parse auth state from localStorage:", error);
                localStorage.removeItem("authState");
            }
        }

        return {
            token: "",
            user: {} as UserGet,
        };
    });

    useEffect(() => {
        localStorage.setItem("authState", JSON.stringify(authState));

        if (!authState.token) {
            localStorage.removeItem("authState");
        }
    }, [authState]);

    const login = async (
        emailAddress: string,
        passsword: string
    ): Promise<APIResponse<ValidateCredentialsDto>> => {
        const deviceToken = getDeviceToken();

        return fetch(`${baseUrl}/api/v1/auth/login`, {
            method: "POST",
            body: JSON.stringify({
                emailAddress: emailAddress,
                password: passsword,
                deviceToken,
            }),
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
        })
            .then((response) => response.json())
            .then((data: APIResponse<ValidateCredentialsDto>) => {
                // If trusted device bypass, set auth state immediately
                if (data.success && data.data?.bypassMfa && data.data?.loginResponse) {
                    setAuthState(data.data.loginResponse);
                }
                return data;
            });
    };

    const logout = () => {
        setIsLoggingOut(true);
        setLoggingParams(undefined);
        setAuthState({
            token: "",
            user: {} as unknown as UserGet,
        });
        // Device trust survives logout; the API enforces its fixed 24-hour expiry.
    };

    const updateUser = (user: UserGet) => {
        setAuthState((prev) => ({
            ...prev,
            user,
        }));
    };

    const requestNewPassword = async (email: string): Promise<APIResponse<boolean>> => {
        const res = await fetch(`${baseUrl}/api/v1/auth/new-password?email=${email}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        const data = await res.json();

        if (data.success === false) throw new Error(data.message);

        return data;
    };

    const resetPassword = async (
        password: string,
        token: string
    ): Promise<APIResponse<boolean>> => {
        return fetch(
            `${baseUrl}/api/v1/auth/reset-password?token=${encodeURIComponent(token)}&newPassword=${encodeURIComponent(password)}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }
        )
            .then((response) => response.json())
            .then((data: APIResponse<boolean>) => {
                if (data.success === false) throw new Error(data.message);
                return data;
            });
    };

    const validateResetToken = async (token: string): Promise<APIResponse<boolean>> => {
        return fetch(
            `${baseUrl}/api/v1/auth/validate-reset-request?token=${encodeURIComponent(token)}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            }
        )
            .then((response) => response.json())
            .then((data: APIResponse<boolean>) => {
                if (data.success === false) throw new Error(data.message);
                return data;
            });
    };

    const startMfaSetup = async (email: string): Promise<APIResponse<MfaSetupDto>> => {
        return fetch(`${baseUrl}/api/v1/auth/mfa-start-setup`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
        })
            .then((response) => response.json())
            .then((data: APIResponse<MfaSetupDto>) => {
                if (data.success === false) throw new Error(data.message);
                return data;
            });
    };

    const verifyMfaSetup = async (
        code: string,
        secret: string,
        email: string,
        rememberDevice?: boolean
    ): Promise<APIResponse<LoginResponse>> => {
        return fetch(`${baseUrl}/api/v1/auth/mfa-verify-setup`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ code, secret, email, rememberDevice }),
            credentials: "include",
        })
            .then((response) => {
                const deviceTokenHeader = response.headers.get("X-Device-Token");
                if (deviceTokenHeader && rememberDevice) {
                    setDeviceToken(deviceTokenHeader);
                }
                return response.json();
            })
            .then((data: APIResponse<LoginResponse>) => {
                if (data.success === false) throw new Error(data.message);
                setAuthState(data.data);
                return data;
            });
    };

    const verifyMfa = async (
        code: string,
        email: string,
        rememberDevice?: boolean
    ): Promise<APIResponse<LoginResponse>> => {
        return fetch(`${baseUrl}/api/v1/auth/mfa-verify`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ code, email, rememberDevice }),
            credentials: "include",
        })
            .then((response) => {
                const deviceTokenHeader = response.headers.get("X-Device-Token");
                if (deviceTokenHeader && rememberDevice) {
                    setDeviceToken(deviceTokenHeader);
                }
                return response.json();
            })
            .then((data: APIResponse<LoginResponse>) => {
                if (data.success === false) throw new Error(data.message);
                setAuthState(data.data);
                setLoggingParams({
                    user: {
                        id: data.data.user.userId,
                        email: data.data.user.emailAddress,
                        name: data.data.user.fullName,
                        role: data.data.user.userRole,
                    },
                });
                return data;
            });
    };

    const devLogin = async (email: string, password: string): Promise<LoginResponse> => {
        if (!enableDevMfaBypass) {
            throw new Error("Development login is not enabled.");
        }

        return fetch(`${baseUrl}/api/v1/auth/dev-login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ emailAddress: email, password }),
        })
            .then((response) => response.json())
            .then((data: APIResponse<LoginResponse>) => {
                if (data.success === false) throw new Error(data.message);
                setAuthState(data.data);
                return data.data;
            });
    };

    return (
        <AuthContext.Provider
            value={{
                authState,
                login,
                logout,
                updateUser,
                requestNewPassword,
                resetPassword,
                validateResetToken,
                startMfaSetup,
                verifyMfaSetup,
                verifyMfa,
                isLoggingOut,
                ...(enableDevMfaBypass ? { devLogin } : {}),
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
